import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faInstagram,
  faFacebook,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import "./styles.scss";

// Register brand icons (cast any to avoid potential version typing issues between core 6.x & icon 6.x/7.x mixes)
library.add(faInstagram as any, faFacebook as any, faTiktok as any);

export interface FooterProps {
  imageUrl?: string; // logo image
  backgroundUrl?: string; // background image for entire footer
}

// Simple nav items array in case we later want to map or localize
const NAV_LINKS = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Menu", href: "/menu" },
];

export const FooterContentPiece = ({
  imageUrl,
  backgroundUrl,
  footerData,
  navigation,
}: FooterProps & {
  footerData: any;
  navigation?: { title: string; href: string; visible: boolean }[];
}) => {
  imageUrl = footerData?.sowLogo || imageUrl;
  backgroundUrl = footerData?.bkgImg || backgroundUrl;
  const footerLinks = navigation
    ? navigation
        .filter((link) => link.visible)
        .map((link) => ({ label: link.title, href: link.href }))
    : NAV_LINKS;
  return (
    <footer className="relative mt-24 text-sow-white/90 border-t border-sow-gold/20 font-sans overflow-hidden">
      {/* Background image layer */}
      {backgroundUrl && (
        <>
          <img
            src={backgroundUrl}
            alt="Footer background"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/90" aria-hidden="true" />
        </>
      )}
      {!backgroundUrl && (
        <div className="absolute inset-0 bg-[#181515] " aria-hidden="true" />
      )}
      <div className="mx-auto w-full max-w-7xl px-6 py-14 md:py-20 relative">
        <div className="grid gap-14 md:gap-16 lg:gap-20 md:grid-cols-3">
          {/* Column 1: Logo/Image + Social Icons */}
          <div className="flex flex-col items-center text-center">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={footerData?.logoAlt || "SouledOut logo"}
                className="w-48 h-auto object-contain drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] mb-6"
                loading="lazy"
              />
            ) : (
              <div className="w-48 h-28 rounded-xl bg-gradient-to-br from-sow-gold/40 to-sow-darkGold/30 flex items-center justify-center mb-6 shadow-inner">
                <span className="text-sow-gold font-bold tracking-wide text-lg">
                  {footerData?.name}
                </span>
              </div>
            )}
            <div className="flex items-center justify-center md:justify-start gap-5 mt-1">
              <a
                aria-label="Instagram"
                href={
                  footerData?.instagram ||
                  "https://www.instagram.com/souledout_soulfood/"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 hover:bg-sow-gold/20 hover:text-sow-gold transition shadow-inner">
                  <FontAwesomeIcon
                    icon={["fab", "instagram"]}
                    className="h-7 w-7"
                  />
                </span>
              </a>
              <a
                aria-label="Facebook"
                href={
                  footerData?.facebook ||
                  "https://www.facebook.com/souledoutsoulfood"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 hover:bg-sow-gold/20 hover:text-sow-gold transition shadow-inner">
                  <FontAwesomeIcon
                    icon={["fab", "facebook"]}
                    className="h-7 w-7"
                  />
                </span>
              </a>
              <a
                aria-label="TikTok"
                href={
                  footerData?.tiktok ||
                  "https://www.tiktok.com/@souled.out.soulfo"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="group"
              >
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 ring-1 ring-white/10 hover:bg-sow-gold/20 hover:text-sow-gold transition shadow-inner">
                  <FontAwesomeIcon
                    icon={["fab", "tiktok"]}
                    className="h-7 w-7"
                  />
                </span>
              </a>
            </div>
          </div>

          {/* Column 2: Title + Nav + Separator + Copy */}
          <div className="flex flex-col items-center text-center relative">
            <h2 className="hammer text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wide text-sow-gold mb-6">
              {footerData?.fullName}
            </h2>
            <nav aria-label="Footer navigation" className="mb-8">
              <ul className="flex flex-wrap items-center justify-center gap-10 text-lg font-semibold">
                {footerLinks.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-sow-gold/60 hover:after:w-full after:transition-all"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="w-48 h-[2px] bg-gradient-to-r from-transparent via-sow-gold/80 to-transparent mb-6" />
            <p className="text-sm sm:text-base tracking-wide font-medium text-sow-white/70">
              {footerData?.registry}
            </p>
            <p className="text-xs sm:text-sm mt-2 text-sow-white/50 font-semibold">
              powered by:{" "}
              <a
                href="https://www.cmjsoftware.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sow-gold underline hover:text-sow-gold/80 transition-colors"
              >
                CMJ Software
              </a>
            </p>
          </div>

          {/* Column 3: Address + Hours */}
          <div className="flex flex-col items-center text-center gap-4 text-lg leading-relaxed relative">
            <address className="not-italic space-y-2">
              <p className="font-medium">{footerData?.addressTop}</p>
              <p className="font-medium">{footerData?.addressBottom}</p>
              <p>
                <a
                  href={`mailto:${
                    footerData?.email || "Flavors@SouledOutSoulFood.com"
                  }`}
                  className="hover:text-sow-gold transition-colors font-medium"
                >
                  {footerData?.email}
                </a>
              </p>
              <p>
                <a
                  href={`tel:${
                    (footerData?.phone || footerData?.phoneNumber)?.replace(
                      /[^+0-9]/g,
                      ""
                    ) || "17347897055"
                  }`}
                  className="hover:text-sow-gold transition-colors font-medium text-xl"
                >
                  {footerData?.phone || footerData?.phoneNumber}
                </a>
              </p>
            </address>
            <p className="mt-5 max-w-xs text-base text-sow-white/70 leading-snug font-medium">
              {footerData?.hours}
            </p>
          </div>
        </div>
        {/* Bottom subtle divider & baseline note (optional) */}
      </div>
    </footer>
  );
};
